import React, { useState, useEffect, useCallback } from "react";
import {
  Typography,
  Button,
  TextField,
  Grid,
  FormGroup,
  InputLabel,
  Select,
} from "@mui/material";
import useStyles from "./style";
import { useFormik } from "formik";
import * as yup from "yup";
import { showLoader, hideLoader } from "Redux/Loader/actions";
import { showSnackbar } from "Redux/SnackBar/actions";
import { setNewtimezone, setSystemlatitude, setSystemlongitude } from "Redux/TimeZone/actions";
import { useDispatch } from "react-redux";
import _ from "lodash";
import MenuItem from "@mui/material/MenuItem";

//Api routes
import settingsApi from "Services/Settings";

//Data
import countryCodeData from "./data/countryCode/index.json";

//
import timeZoneData from "./data/timeZone.json";

const Settings = () => {
  const classes = useStyles();
  const dispatch = useDispatch();

  // States
  const [values, setValues] = useState([]);

  //

  const handleSettings = (formValues) => {
    dispatch(showLoader("Loading please wait..."));

    const timeZ = timeZoneData.find(
      (zone) => zone.utc[0] === formValues.timeZone
    );

    let timeZoneOffset;

    if (timeZ) {
      timeZoneOffset = timeZ.offset;
    } else {
      console.error(`Time zone with value '${formValues.timeZone}' not found`);
    }

    function offsetToMinutes(offset) {
      return offset * 60;
    }

    const data = {
      isMailEnable: formValues?.isMailEnable === "ENABLE" ? 1 : 0,
      isSmsEnable: formValues?.isSmsEnable === "ENABLE" ? 1 : 0,
      countryCode: formValues?.countryCode,
      driverAcceptenceTimeOut: formValues?.driverAcceptTimeout,
      driverSearchRadius: formValues?.driverSearchRadius,
      distanceUnit: formValues?.distanceUnit,
      autoAssign: formValues?.autoAssign === "ENABLE" ? 1 : 0,
      siteCountry: formValues?.country,
      address: formValues?.address,
      mapZoomLevel: formValues?.mapZoomLevel,
      timeZone: formValues?.timeZone,
      timeZoneOffset: offsetToMinutes(timeZoneOffset),
      availableTips: formValues?.availableTips,
      driver_increment: formValues?.driver_increment,
      findDriverBeforeScheduleMinutes: formValues?.findDriverBeforeScheduleMinutes,
    };
    const onSuccess = (res) => {
      dispatch(hideLoader());
      if (res?.data?.status === true) {
        dispatch(
          showSnackbar({
            message: res?.data?.message || "Updated settings successfully",
            autoHideDuration: 3000,
            anchorOrigin: {
              vertical: "top",
              horizontal: "right",
            },
            variant: "success",
          })
        );
        formik?.resetForm();
        setValues(null);
        getMasterValues();
      }
    };
    const onFailure = (err) => {
      dispatch(hideLoader());
      dispatch(
        showSnackbar({
          message: err?.response?.data?.message || "Error while update settings",
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "error",
        })
      );
      //console.log("settings value update api", err);
    };
    settingsApi.updateBusinessSettings({ ...data }).then(onSuccess, onFailure);
  };

  const formik = useFormik({
    initialValues: {
      isMailEnable: "ENABLE",
      isSmsEnable: "ENABLE",
      countryCode: "0",
      driverAcceptTimeout: "0",
      driverSearchRadius: "0",
      distanceUnit: "KM",
      autoAssign: "ENABLE",
      country: "",
      address: "",
      availableTips: ["", "", "", ""],
      mapZoomLevel: "10",
      timeZone: "America/Anchorage",
      driver_increment: "1.0",
      findDriverBeforeScheduleMinutes: "5",
    },
    enableReinitialize: true,
    validationSchema: yup.object({
      availableTips: yup
        .array()
        .of(yup.string().nullable().label("Tip Level").default("")),
      timeZone: yup.string().required("This field is required"),
      isMailEnable: yup.string().required("This field is required"),
      isSmsEnable: yup.string().required("This field is requried"),
      driverAcceptTimeout: yup
        .number()
        .typeError("Must be a number")
        .min(0, "Must be greater than or equal to 0")
        .max(1000, "Must be less than or equal to 1000"),
      driver_increment: yup
        .number()
        .typeError("Must be a number")
        .min(0, "Must be greater than or equal to 0"),
      findDriverBeforeScheduleMinutes: yup
        .number()
        .typeError("Must be a number")
        .min(0, "Must be greater than or equal to 0"),
      driverSearchRadius: yup
        .number()
        .typeError("Must be a number")
        .min(0, "Must be greater than or equal to 0")
        .max(1000, "Must be less than or equal to 1000"),
      distanceUnit: yup.string().required("This field is required"),
      autoAssign: yup.string().required("This field is required"),
      mapZoomLevel: yup
        .number()
        .typeError("Must be a number")
        .min(0, "Must be greater than or equal to 0")
        .max(1000, "Must be less than or equal to 1000"),
      availableTips: yup
        .array()
        .of(
          yup
            .number()
            .typeError("Must be a number")
            .min(0, "Must be greater than or equal to 0")
            .max(1000, "Must be less than or equal to 1000")
        ),
    }),
    onSubmit: handleSettings,
  });

  // Get Business Settings
  const getMasterValues = useCallback(() => {
    dispatch(showLoader("Loading please wait..."));
    const onSuccess = (res) => {
      dispatch(hideLoader());
      if (res?.data?.status === true) {
        dispatch(setNewtimezone(res?.data?.data?.timeZone));
        dispatch(setSystemlatitude(res?.data?.data?.latitude));
        dispatch(setSystemlongitude(res?.data?.data?.longitude));
        setValues(res?.data?.data);
      } else {
        setValues([]);
      }
    };
    const onFailure = (err) => {
      dispatch(hideLoader());
      dispatch(
        showSnackbar({
          message: err?.response?.data?.message || "Failed to fetch data",
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "error",
        })
      );
    };
    settingsApi.getBusinessSettings().then(onSuccess, onFailure);
  }, []);

  useEffect(() => {
    getMasterValues();
  }, [getMasterValues]);

  // Formik - Set fetched values for master
  useEffect(() => {
    if (!_.isEmpty(values)) {
      //console.log("values ", values);

      if (values?.isMailEnable === 1) {
        formik.setFieldValue("isMailEnable", "ENABLE");
      } else {
        formik.setFieldValue("isMailEnable", "DISABLE");
      }
      if (values?.isSmsEnable === 1) {
        formik.setFieldValue("isSmsEnable", "ENABLE");
      } else {
        formik.setFieldValue("isSmsEnable", "DISABLE");
      }

      formik.setFieldValue("countryCode", values?.countryCode);
      formik.setFieldValue(
        "driverAcceptTimeout",
        values?.driverAcceptenceTimeOut
      );
      formik.setFieldValue("driverSearchRadius", values?.driverSearchRadius);

      formik.setFieldValue("distanceUnit", values?.distanceUnit);

      if (values?.autoAssign === 1) {
        formik.setFieldValue("autoAssign", "ENABLE");
      } else {
        formik.setFieldValue("autoAssign", "DISABLE");
      }

      formik.setFieldValue("country", values?.siteCountry);
      formik.setFieldValue("state", values?.state);
      formik.setFieldValue("city", values?.city);
      formik.setFieldValue("address", values?.address);
      formik.setFieldValue("mapZoomLevel", values?.mapZoomLevel);
      formik.setFieldValue("timeZone", values?.timeZone);
      formik.setFieldValue("availableTips", values?.availableTips);
      formik.setFieldValue("driver_increment", values?.driver_increment || "1.0");
      formik.setFieldValue("findDriverBeforeScheduleMinutes", values?.findDriverBeforeScheduleMinutes || "5");

      // setTimeZoneOffset(values?.timeZoneOffset);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values]);

  return (
    <>
      <br></br>
      <br></br>
      <Grid container className={classes.cardContainer}>
<Grid
          container item
          xs={12}
          md={12}
          lg={12}
          xl={12}
          className={classes.businessSettingsContainer}
        >
          <Grid
            item
            xs={12}
            md={12}
            lg={12}
            xl={12}
            className={classes.container}
          >
            <div style={{ padding: "15px" }}>
              <Typography variant="h5">Business Settings</Typography>
            </div>
          </Grid>
          <Grid item xs={12} md={12} lg={12} xl={12}>
            {/* Enable Mail */}
            <Grid
              item
              xs={12}
              md={6}
              lg={6}
              xl={6}
              className={classes.fieldWrapper}
            >
              <TextField
                name="isMailEnable"
                id="isMailEnable"
                select
                label="Enable Mail"
                value={formik?.values?.isMailEnable}
                defaultValue={formik?.values?.isMailEnable}
                onChange={formik?.handleChange}
                error={
                  formik?.errors?.isMailEnable && formik?.touched?.isMailEnable
                }
                helperText={
                  formik?.errors?.isMailEnable &&
                  formik?.touched?.isMailEnable &&
                  formik?.errors?.isMailEnable
                }
                fullWidth
                variant="outlined"
                size="small"
              >
                {["ENABLE", "DISABLE"].map((option) => (
                  <MenuItem key={option} value={option}>
                    {option === "ENABLE" ? "Enable" : "Disable"}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Enable Sms */}
            <Grid
              item
              xs={12}
              md={6}
              lg={6}
              xl={6}
              className={classes.fieldWrapper}
            >
              <TextField
                name="isSmsEnable"
                id="isSmsEnable"
                select
                label="Sms Enable"
                value={formik?.values?.isSmsEnable}
                defaultValue={formik?.values?.isSmsEnable}
                onChange={formik?.handleChange}
                error={
                  formik?.errors?.isSmsEnable && formik?.touched?.isSmsEnable
                }
                helperText={
                  formik?.errors?.isSmsEnable &&
                  formik?.touched?.isSmsEnable &&
                  formik?.errors?.isSmsEnable
                }
                fullWidth
                variant="outlined"
                size="small"
              >
                {["ENABLE", "DISABLE"].map((option) => (
                  <MenuItem key={option} value={option}>
                    {option === "ENABLE" ? "Enable" : "Disable"}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Country Code */}
            <Grid
              item
              xs={12}
              md={6}
              lg={6}
              xl={6}
              className={classes.fieldWrapper}
            >
              <TextField
                name="countryCode"
                id="countryCode"
                value={formik?.values?.countryCode}
                defaultValue={formik?.values?.countryCode}
                onChange={formik?.handleChange}
                type={"text"}
                fullWidth
                variant="outlined"
                size="small"
                label="Country Code"
                error={
                  formik?.errors?.countryCode && formik?.touched?.countryCode
                }
                helperText={
                  formik?.errors?.countryCode &&
                  formik?.touched?.countryCode &&
                  formik?.errors?.countryCode
                }
              />
            </Grid>

            {/* Driver Accept timeout */}
            <Grid
              item
              xs={12}
              md={6}
              lg={6}
              xl={6}
              className={classes.fieldWrapper}
            >
              <TextField
                name="driverAcceptTimeout"
                id="driverAcceptTimeout"
                value={formik?.values?.driverAcceptTimeout}
                defaultValue={formik?.values?.driverAcceptTimeout}
                onChange={formik?.handleChange}
                type={"text"}
                fullWidth
                variant="outlined"
                size="small"
                label="Driver Accept Timeout"
                error={
                  formik?.errors?.driverAcceptTimeout &&
                  formik?.touched?.driverAcceptTimeout
                }
                helperText={
                  formik?.errors?.driverAcceptTimeout &&
                  formik?.touched?.driverAcceptTimeout &&
                  formik?.errors?.driverAcceptTimeout
                }
              />
            </Grid>

            {/* Driver Search Radius */}

            <Grid
              item
              xs={12}
              md={6}
              lg={6}
              xl={6}
              className={classes.fieldWrapper}
            >
              <TextField
                name="driverSearchRadius"
                id="driverSearchRadius"
                value={formik?.values?.driverSearchRadius}
                defaultValue={formik?.values?.driverSearchRadius}
                onChange={formik?.handleChange}
                type="number"
                step="0.01"
                fullWidth
                variant="outlined"
                size="small"
                label="Driver Search Radius"
                error={
                  formik?.errors?.driverSearchRadius &&
                  formik?.touched?.driverSearchRadius
                }
                helperText={
                  formik?.errors?.driverSearchRadius &&
                  formik?.touched?.driverSearchRadius &&
                  formik?.errors?.driverSearchRadius
                }
              />
            </Grid>

            <Grid
              item
              xs={12}
              md={6}
              lg={6}
              xl={6}
              className={classes.fieldWrapper}
            >
              <TextField
                name="distanceUnit"
                id="distanceUnit"
                select
                label="Distance Unit"
                value={formik?.values?.distanceUnit}
                defaultValue={formik?.values?.distanceUnit}
                onChange={formik?.handleChange}
                error={
                  formik?.errors?.distanceUnit && formik?.touched?.distanceUnit
                }
                helperText={
                  formik?.errors?.distanceUnit &&
                  formik?.touched?.distanceUnit &&
                  formik?.errors?.distanceUnit
                }
                fullWidth
                variant="outlined"
                size="small"
              >
                {["KM", "MILES"].map((option) => (
                  <MenuItem key={option} value={option}>
                    {option === "KM" ? "km" : "Miles"}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
          <Grid item md={12} xl={12} lg={12}>
            <Grid
              item
              xs={12}
              md={6}
              lg={6}
              xl={6}
              className={classes.fieldWrapper}
            >
              <TextField
                name="autoAssign"
                id="autoAssign"
                select
                label="Auto Assign"
                value={formik?.values?.autoAssign}
                defaultValue={formik?.values?.autoAssign}
                onChange={formik?.handleChange}
                error={
                  formik?.errors?.autoAssign && formik?.touched?.autoAssign
                }
                helperText={
                  formik?.errors?.autoAssign &&
                  formik?.touched?.autoAssign &&
                  formik?.errors?.autoAssign
                }
                fullWidth
                variant="outlined"
                size="small"
              >
                {["ENABLE", "DISABLE"].map((option) => (
                  <MenuItem key={option} value={option}>
                    {option === "ENABLE" ? "Enable" : "Disable"}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid
              item
              xs={12}
              md={6}
              lg={6}
              xl={6}
              className={classes.fieldWrapper}
            >
              {/* <TextField
                name="country"
                id="country"
                value={formik?.values?.country}
                defaultValue={formik?.values?.country}
                onChange={formik?.handleChange}
                type="text"
                fullWidth
                variant="outlined"
                size="small"
                label="Country"
                error={formik?.errors?.country && formik?.touched?.country}
                helperText={
                  formik?.errors?.country &&
                  formik?.touched?.country &&
                  formik?.errors?.country
                }
              /> */}

              <TextField
                inputProps={{
                  form: {
                    autocomplete: 'off',
                  },
                }}
                name="country"
                id="country"
                select
                label="Country"
                value={formik?.values?.country || ""}
                onChange={formik?.handleChange}
                error={
                  formik?.errors?.country && formik?.touched?.country
                }
                helperText={
                  formik?.errors?.country &&
                  formik?.touched?.country &&
                  formik?.errors?.country
                }
                fullWidth
                variant="outlined"
                size="small"
              >
                {countryCodeData.map((country) => (
                  <MenuItem
                    key={country.code}
                    value={country.name}
                  >
                    {`${country.code} (${country.name})`}
                  </MenuItem>
                ))}
              </TextField>

            </Grid>

            <Grid
              item
              xs={12}
              md={6}
              lg={6}
              xl={6}
              className={classes.fieldWrapper}
            >
              <TextField
                name="address"
                id="address"
                value={formik?.values?.address}
                defaultValue={formik?.values?.address}
                onChange={formik?.handleChange}
                type="text"
                fullWidth
                variant="outlined"
                size="small"
                label="Address"
                error={formik?.errors?.address && formik?.touched?.address}
                helperText={
                  formik?.errors?.address &&
                  formik?.touched?.address &&
                  formik?.errors?.address
                }
              />
            </Grid>

            <Grid
              item
              xs={12}
              md={6}
              lg={6}
              xl={6}
              className={classes.fieldWrapper}
            >
              <TextField
                name="mapZoomLevel"
                id="mapZoomLevel"
                value={formik?.values?.mapZoomLevel}
                defaultValue={formik?.values?.mapZoomLevel}
                onChange={formik?.handleChange}
                type="text"
                fullWidth
                variant="outlined"
                size="small"
                label="Map Zoom Level"
                error={
                  formik?.errors?.mapZoomLevel && formik?.touched?.mapZoomLevel
                }
                helperText={
                  formik?.errors?.mapZoomLevel &&
                  formik?.touched?.mapZoomLevel &&
                  formik?.errors?.mapZoomLevel
                }
              />
            </Grid>

            {/* Driver Increment */}
            <Grid
              item
              xs={12}
              md={6}
              lg={6}
              xl={6}
              className={classes.fieldWrapper}
            >
              <TextField
                name="driver_increment"
                id="driver_increment"
                value={formik?.values?.driver_increment}
                defaultValue={formik?.values?.driver_increment}
                onChange={formik?.handleChange}
                type="number"
                step="0.01"
                fullWidth
                variant="outlined"
                size="small"
                label="Driver Increment"
                error={
                  formik?.errors?.driver_increment && formik?.touched?.driver_increment
                }
                helperText={
                  formik?.errors?.driver_increment &&
                  formik?.touched?.driver_increment &&
                  formik?.errors?.driver_increment
                }
              />
            </Grid>

            {/* Find Driver Before Schedule Minutes */}
            <Grid
              item
              xs={12}
              md={6}
              lg={6}
              xl={6}
              className={classes.fieldWrapper}
            >
              <TextField
                name="findDriverBeforeScheduleMinutes"
                id="findDriverBeforeScheduleMinutes"
                value={formik?.values?.findDriverBeforeScheduleMinutes}
                defaultValue={formik?.values?.findDriverBeforeScheduleMinutes}
                onChange={formik?.handleChange}
                type="number"
                fullWidth
                variant="outlined"
                size="small"
                label="Find Driver Before Schedule Minutes"
                error={
                  formik?.errors?.findDriverBeforeScheduleMinutes &&
                  formik?.touched?.findDriverBeforeScheduleMinutes
                }
                helperText={
                  formik?.errors?.findDriverBeforeScheduleMinutes &&
                  formik?.touched?.findDriverBeforeScheduleMinutes &&
                  formik?.errors?.findDriverBeforeScheduleMinutes
                }
              />
            </Grid>

            {/* timeZone Offset */}
            <Grid
              item
              xs={12}
              md={6}
              lg={6}
              xl={6}
              className={classes.fieldWrapper}
            >
              <Grid
                item
                xs={12}
                md={12}
                lg={12}
                xl={12}
                className={classes.fieldWrapper}
              >
                <TextField
                  name="timeZone"
                  id="timeZone"
                  select
                  label="Time Zone"
                  value={formik.values.timeZone}
                  defaultValue={formik.values.timeZone}
                  onChange={formik.handleChange}
                  error={formik.errors.timeZone && formik.touched.timeZone}
                  helperText={
                    formik.errors.timeZone &&
                    formik.touched.timeZone &&
                    formik.errors.timeZone
                  }
                  fullWidth
                  variant="outlined"
                  size="small"
                >
                  {Object.keys(timeZoneData).map((timeZoneValue) => (
                    <MenuItem
                      key={timeZoneValue}
                      value={timeZoneData[timeZoneValue].utc[0]}
                    >
                      {timeZoneData[timeZoneValue].text} (
                      {timeZoneData[timeZoneValue].offset})
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>

            {/* Available Tips */}
            <Grid item xs={12} md={12} lg={12} xl={12}>
              <Typography
                style={{
                  color: "rgba(0, 0, 0, 0.6)",
                  fontSize: "13px",
                  padding: "0px 5px",
                }}
              >
                Available Tips
              </Typography>
              <br></br>

              <Grid container spacing={2}>
                {["1", "2", "3", "4"].map((tipLevel, index) => (
                  <Grid
                    item
                    xs={12}
                    md={3}
                    lg={3}
                    xl={3}
                    key={index}
                    style={{ padding: "5px 20px" }}
                  >
                    <TextField
                      name={`availableTips[${index}]`}
                      id={`availableTips-${index}`}
                      value={formik?.values?.availableTips[index]}
                      defaultValue={formik?.values?.availableTips[index]}
                      onChange={formik?.handleChange}
                      type="text"
                      fullWidth
                      variant="outlined"
                      size="small"
                      label={`Tip ${tipLevel}`}
                      error={
                        formik?.errors?.availableTips &&
                        formik?.errors?.availableTips[index] &&
                        formik?.touched?.availableTips &&
                        formik?.touched?.availableTips[index]
                      }
                      helperText={
                        formik?.errors?.availableTips &&
                        formik?.errors?.availableTips[index] &&
                        formik?.touched?.availableTips &&
                        formik?.touched?.availableTips[index] &&
                        formik?.errors?.availableTips[index]
                      }
                    />
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12} md={6} lg={12} xl={12} className={classes.btn}>
          <Button variant="contained" onClick={formik.handleSubmit}>
            Update
          </Button>
        </Grid>
      </Grid>

      <br></br>
      <br></br>
      <br></br>
    </>
  );
};

export default Settings;
